import { Box, Flex, Textarea } from '@chakra-ui/react';
import { useField } from 'formik';
import { useRef } from 'react';
import { MaxLengthCounter } from './MaxLengthCounter';

export const TextareaField = ({ item, field, formik }) => {
  const localInputRef = useRef<HTMLTextAreaElement>(null);

  if (!item) return null;

  const {
    name,
    isEditable = true,
    placeholder = '',
    label,
    style,
    disabled,
    showErrorStyles = false,
    isRequired,
    maxLength,
    styleVersion = 'v1',
  } = item;

  const [_field, meta, helpers] = useField(name); // eslint-disable-line
  const { setTouched } = helpers;
  const { submitCount } = formik || {};
  const { value, touched } = meta || {};
  const showError =
    (submitCount > 0 || touched) &&
    (!(value?.length > 0) || value?.length > maxLength) &&
    !showErrorStyles &&
    isRequired;
  if (!item) return null;

  const defaultStyles = {
    fontSize: '14px',
    paddingInlineStart: '.75em',
    pt: '1.125em',
    pb: '1.125em',
    height: '3.5em',
    border: '1px solid',
    borderColor: showError ? 'error-200' : 'secondary-100',
    borderRadius: '6px',
    bg: showError ? 'pastel-1-light' : 'base-white',
    color: showError ? 'error-200' : 'secondary-700',
    outlineOffset: '0px !important',
    transition: 'background-color 0.15s ease, border-color 0.15s ease',
    _placeholder: {
      opacity: 1,
      fontSize: '14px',
      fontWeight: 'medium',
      color: showError ? 'error-100' : 'secondary-500',
    },
    _hover: {
      borderColor: showError ? 'error-200' : 'secondary-300',
      bg: showError ? 'pastel-1-light' : 'secondary-25',
      boxShadow: 'none',
    },
    _focus: {
      borderColor: showError ? 'error-200' : 'secondary-300',
      bg: showError ? 'pastel-1-light' : 'secondary-25',
      boxShadow: 'none',
      outline: 'none',
    },
    _invalid: {
      borderColor: 'error-200',
      boxShadow: 'none !important',
    },
  };

  const versionV2 = {
    fontSize: '14px',
    paddingInlineStart: '.75em',
    pt: '1.125em',
    pb: '1.125em',
    height: '3.5em',
    outlineOffset: '0px !important',
    borderRadius: '8px',
    border: showError
      ? '1px solid #C03744 !important'
      : '1px solid #E5E5E5 !important',
    color: showError ? '#C03744 !important' : 'secondary-700 !important',
    _focus: {
      border: '1px solid',
      borderColor: showError ? '#C03744' : 'secondary-300 !important',
      boxShadow: 'none !important',
      backgroundColor:
        value && value.length > 0
          ? '#fff'
          : showError
            ? '#FFEBE5'
            : 'secondary-25',
    },
    _hover: {
      border: showError
        ? '1px solid #C03744 !important'
        : '1px solid #E5E5E5 !important',
      boxShadow: 'none !important',
    },
    _placeholder: {
      opacity: 1,
      fontSize: '14px',
      color: showError ? '#C03744 !important' : 'secondary-500 !important',
    },
    _invalid: {
      border: '1px solid #C03744 !important',
      boxShadow: 'none !important',
    },
  };

  const styleVersionMap = {
    v1: defaultStyles,
    v2: versionV2,
  };

  const textAreaStyles = styleVersionMap[styleVersion] || defaultStyles;

  return (
    <Box w="100%">
      <Textarea
        {...field}
        ref={localInputRef}
        id={name}
        onClick={() => {
          if (localInputRef?.current) {
            localInputRef.current?.focus();
          }
        }}
        maxLength={maxLength}
        maxHeight="200px"
        disabled={disabled || !isEditable}
        value={field.value || ''}
        placeholder={placeholder || `Enter ${label?.toLowerCase()}`}
        background="#fff"
        onFocus={() => setTouched(false)}
        onBlur={() => setTouched(true)}
        {...textAreaStyles}
        {...style}
        data-test-id="textarea-field"
      />
      {/* The native maxLength truncates silently, so show what the cap is. */}
      {!!maxLength && (
        <Flex justifyContent="flex-end" mt="4px">
          <MaxLengthCounter
            currentLength={String(field?.value ?? '').length}
            maxLength={maxLength}
            disabled={disabled || !isEditable}
            showError={showError}
          />
        </Flex>
      )}
    </Box>
  );
};
